import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import i18n from '../i18n';
import { formatTimeGR } from './shared/dateUtils';

const tr = i18n.t.bind(i18n);

const FACTOR_LABELS = {
  capacity: (m) => tr('Capacity fit: {{people}} people on {{capacity}} seats', m),
  personal: (m) => (m.visits > 0
    ? tr('Client history: {{areaVisits}}/{{visits}} visits in {{areaName}}', m)
    : tr('New client — no personal history yet')),
  seasonal: (m) => tr('Current guest preference for {{areaName}}: {{popularity}}%', m),
  vip: (m) => tr('Client rating {{rate}}★ × table demand {{tablePopularity}}%', m),
  combo: (m) => tr('Joined tables ({{tables}})', m),
};

const scoreColor = (score) =>
  score >= 60 ? '#198754' : score >= 40 ? '#d6a400' : '#b02a37';

const optionLabel = (opt) =>
  opt.tables.map((t) => t.number).join(' + ') +
  (opt.tables[0]?.area_name ? ` · ${opt.tables[0].area_name}` : '') +
  ` · ${opt.score}${tr('pts')}`;

const AutoAssignModal = ({ isOpen, onClose, onApplied, isMobile }) => {
  const todayISO = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const [date, setDate] = useState(todayISO);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [selected, setSelected] = useState({});   // reservationId -> bool
  const [choices, setChoices] = useState({});     // reservationId -> option index (0=proposal, 1..=alt)
  const [openDetails, setOpenDetails] = useState({});
  const [applied, setApplied] = useState({});     // reservationId -> 'ok' | error string

  const load = useCallback(async (d) => {
    setLoading(true);
    setApplied({});
    try {
      const { data } = await axios.get(
        tr('{{API_BASE_URL}}/auto-assign/suggestions', { API_BASE_URL: config.API_BASE_URL }),
        { params: { date: d } }
      );
      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setSuggestions(list);
      const sel = {}; const ch = {};
      list.forEach((s) => {
        sel[s.reservation.id] = Boolean(s.proposal);
        ch[s.reservation.id] = 0;
      });
      setSelected(sel);
      setChoices(ch);
    } catch (e) {
      console.error('auto-assign suggestions failed', e);
      toast.error(tr('Failed to load suggestions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) load(date); }, [isOpen, date, load]);

  const optionFor = (s) => {
    const idx = choices[s.reservation.id] || 0;
    return idx === 0 ? s.proposal : (s.alternatives[idx - 1] || s.proposal);
  };

  const applyAssignments = async (list) => {
    if (!list.length || applying) return;
    setApplying(true);
    try {
      const { data } = await axios.post(
        tr('{{API_BASE_URL}}/auto-assign/apply', { API_BASE_URL: config.API_BASE_URL }),
        { assignments: list }
      );
      const next = { ...applied };
      (data?.results || []).forEach((r) => { next[r.reservation] = r.ok ? 'ok' : r.error; });
      setApplied(next);

      const okCount = (data?.results || []).filter((r) => r.ok).length;
      const failCount = (data?.results || []).length - okCount;
      if (okCount) toast.success(tr('Tables assigned: {{n}}', { n: okCount }), { autoClose: 3000 });
      if (failCount) toast.warning(tr('Not assigned (conflict): {{n}} — refresh suggestions', { n: failCount }));
      if (okCount && onApplied) onApplied();
    } catch (e) {
      console.error('auto-assign apply failed', e);
      toast.error(e?.response?.data?.error || tr('Failed to apply assignments.'));
    } finally {
      setApplying(false);
    }
  };

  const applyOne = (s) => {
    const opt = optionFor(s);
    if (opt) applyAssignments([{ reservation: s.reservation.id, tableIds: opt.tableIds }]);
  };

  const applySelected = () => {
    const list = suggestions
      .filter((s) => selected[s.reservation.id] && !applied[s.reservation.id] && optionFor(s))
      .map((s) => ({ reservation: s.reservation.id, tableIds: optionFor(s).tableIds }));
    applyAssignments(list);
  };

  const pendingCount = suggestions.filter(
    (s) => selected[s.reservation.id] && applied[s.reservation.id] !== 'ok' && optionFor(s)
  ).length;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 1000 },
        content: isMobile
          ? { inset: '0px', margin: 'auto', padding: '10px', border: 0, borderRadius: 10, height: '88vh', maxWidth: '94vw', maxHeight: '88vh', overflow: 'auto' }
          : { width: '900px', margin: 'auto', overflowX: 'hidden', borderRadius: 10, height: '84vh' },
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h3 style={{ margin: 0 }}>{'✨'} {tr('Smart table assignment')}</h3>
        <input
          type="date" value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid gray' }}
        />
        {loading && <span style={{ opacity: 0.6 }}>{tr('Loading...')}</span>}
        <button className="rlToolbarBtn" style={{ marginLeft: 'auto' }} onClick={onClose}>{tr('Close')}</button>
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 13, opacity: 0.7 }}>
        {tr('Proposals are ranked by client rating & loyalty, personal area history, current guest preferences and capacity fit. Review, adjust and confirm.')}
      </p>

      {!loading && suggestions.length === 0 && (
        <p style={{ opacity: 0.6 }}>{tr('All reservations of this date already have tables.')}</p>
      )}

      {suggestions.map((s) => {
        const r = s.reservation;
        const opt = optionFor(s);
        const state = applied[r.id];
        const details = openDetails[r.id];
        const clientName = r.client ? `${r.client.lastname || ''} ${r.client.firstname || ''}`.trim() : tr('Walk-in / no client');

        return (
          <div key={r.id} style={{
            border: '1px solid #ddd', borderRadius: 10, padding: '8px 10px', marginBottom: 8,
            background: state === 'ok' ? 'rgba(25,135,84,0.07)' : state ? 'rgba(220,53,69,0.07)' : 'white',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <input
                type="checkbox"
                checked={Boolean(selected[r.id]) && state !== 'ok'}
                disabled={state === 'ok' || !opt}
                onChange={(e) => setSelected((p) => ({ ...p, [r.id]: e.target.checked }))}
              />
              <b style={{ minWidth: 44 }}>{formatTimeGR(r.arrival)}</b>
              <span style={{ flex: '1 1 180px' }}>
                {clientName}
                {r.client && (
                  <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.75 }}>
                    {'⭐'}{r.client.rate}
                    {' · '}{r.client.visits} {tr('visits')}
                    {r.client.no_show > 0 && <span style={{ color: '#b02a37' }}> · {r.client.no_show} no-show</span>}
                  </span>
                )}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>{'\u{1F465}'} {r.people}</span>

              {opt ? (
                <>
                  <select
                    value={choices[r.id] || 0}
                    disabled={state === 'ok'}
                    onChange={(e) => setChoices((p) => ({ ...p, [r.id]: Number(e.target.value) }))}
                    style={{ padding: '5px 6px', borderRadius: 6, maxWidth: 260 }}
                  >
                    <option value={0}>{optionLabel(s.proposal)}</option>
                    {s.alternatives.map((alt, i) => (
                      <option key={i} value={i + 1}>{optionLabel(alt)}</option>
                    ))}
                  </select>
                  <b style={{ color: scoreColor(opt.score), minWidth: 30, textAlign: 'right' }}>{opt.score}</b>
                  <button className="rlToolbarBtn" onClick={() => setOpenDetails((p) => ({ ...p, [r.id]: !p[r.id] }))}>
                    {details ? tr('Hide why') : tr('Why?')}
                  </button>
                  {state === 'ok' ? (
                    <b style={{ color: '#198754' }}>{'✓'} {tr('Assigned')}</b>
                  ) : (
                    <button className="rlToolbarBtn" disabled={applying} onClick={() => applyOne(s)}>
                      {tr('Confirm')}
                    </button>
                  )}
                  {state && state !== 'ok' && (
                    <b style={{ color: '#b02a37', fontSize: 12 }}>{tr('Conflict — refresh')}</b>
                  )}
                </>
              ) : (
                <i style={{ color: '#b02a37' }}>{tr('No available table found')}</i>
              )}
            </div>

            {opt && details && (
              <ul style={{ margin: '8px 0 2px 28px', fontSize: 13 }}>
                {opt.factors.map((f, i) => (
                  <li key={i} style={{ opacity: f.points === 0 ? 0.55 : 1 }}>
                    {FACTOR_LABELS[f.key] ? FACTOR_LABELS[f.key](f.meta || {}) : f.key}
                    {' — '}<b style={{ color: f.points < 0 ? '#b02a37' : 'inherit' }}>
                      {f.points > 0 ? '+' : ''}{f.points}{tr('pts')}
                    </b>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}

      {suggestions.length > 0 && (
        <div style={{ position: 'sticky', bottom: 0, background: 'white', padding: '10px 0 2px', display: 'flex', gap: 10 }}>
          <button
            className="rlToolbarBtn rlToolbarBtnPrimary"
            disabled={applying || pendingCount === 0}
            onClick={applySelected}
          >
            {applying ? tr('Applying...') : tr('Apply selected ({{n}})', { n: pendingCount })}
          </button>
          <button className="rlToolbarBtn" disabled={loading} onClick={() => load(date)}>
            {tr('Refresh suggestions')}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default AutoAssignModal;
